
import React, { useState, useMemo } from 'react';
import { format, addDays, startOfWeek, endOfWeek, isWithinInterval, isEqual, addWeeks, subWeeks } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Employee, Shift } from '@/utils/mockData';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChevronLeft, ChevronRight, Plus, Clock, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import ShiftSignup from './ShiftSignup';

interface CalendarProps {
  shifts: Shift[];
  employees: Employee[];
  onAddShift: (shift: Shift) => void;
  onUpdateShift: (shift: Shift) => void;
  onDeleteShift: (shiftId: string) => void;
  onRequestShift?: (shift: Partial<Shift>) => void;
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 8);
const CELL_HEIGHT = 80;
const HOUR_HEIGHT = 60;

const Calendar: React.FC<CalendarProps> = ({
  shifts,
  employees,
  onAddShift,
  onUpdateShift,
  onDeleteShift,
  onRequestShift,
}) => {
  const { user, isAdmin, isManager } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isNewShiftDialogOpen, setIsNewShiftDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [newShift, setNewShift] = useState<Partial<Shift>>({
    startTime: new Date(),
    endTime: new Date(),
    employeeId: '',
    position: '',
    notes: '',
  });

  // Calculate the current week dates
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  // Filter shifts for current week
  const currentWeekShifts = useMemo(() => {
    return shifts.filter(shift => 
      isWithinInterval(shift.startTime, {
        start: weekStart,
        end: endOfWeek(weekEnd, { weekStartsOn: 1 })
      })
    );
  }, [shifts, weekStart, weekEnd]);

  // Navigate between weeks
  const goToNextWeek = () => {
    setCurrentDate(addWeeks(currentDate, 1));
  };

  const goToPreviousWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format the week range for display
  const formatWeekRange = () => {
    const start = format(weekStart, 'd MMMM', { locale: nl });
    const end = format(weekEnd, 'd MMMM yyyy', { locale: nl });
    return `${start} - ${end}`;
  };

  // Handle opening the new shift dialog
  const handleCellClick = (date: Date, hour: number) => {
    if (!isAdmin && !isManager) return;
    
    const clickedDate = new Date(date);
    clickedDate.setHours(hour, 0, 0, 0);
    
    setSelectedDate(clickedDate);
    setNewShift({
      startTime: clickedDate,
      endTime: new Date(clickedDate.getTime() + 4 * 60 * 60 * 1000), // 4 hours shift by default
      employeeId: '',
      position: '',
      notes: '',
    });
    
    setIsNewShiftDialogOpen(true);
  };

  // Handle shift request from employee
  const handleShiftRequest = (date: Date, startTime: Date, endTime: Date, notes: string) => {
    if (!user) return;
    
    const shiftRequest: Partial<Shift> = {
      employeeId: user.id,
      startTime,
      endTime,
      position: notes ? 'Aangevraagd: ' + notes : 'Aangevraagd',
      notes,
    };
    
    if (onRequestShift) {
      onRequestShift(shiftRequest);
      toast.success('Beschikbaarheid succesvol doorgegeven');
    }
  };

  // Handle form input changes
  const handleShiftInputChange = (field: string, value: string | Date) => {
    setNewShift(prev => ({ ...prev, [field]: value }));
  };

  // Handle employee selection
  const handleEmployeeSelect = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    setNewShift(prev => ({ 
      ...prev, 
      employeeId, 
      position: employee?.position || prev.position 
    }));
  };

  // Save new shift
  const handleSaveShift = () => {
    if (!newShift.employeeId || !newShift.startTime || !newShift.endTime) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    
    const shiftToSave: Shift = {
      id: `shift-${Date.now()}`,
      employeeId: newShift.employeeId as string,
      startTime: newShift.startTime as Date,
      endTime: newShift.endTime as Date,
      position: newShift.position as string,
      notes: newShift.notes,
    };
    
    onAddShift(shiftToSave);
    setIsNewShiftDialogOpen(false);
    toast.success('Dienst toegevoegd');
  };

  // Get the positions of shifts in the calendar grid
  const getShiftPosition = (shift: Shift, day: Date) => {
    const isSameDay = isEqual(
      new Date(shift.startTime.getFullYear(), shift.startTime.getMonth(), shift.startTime.getDate()),
      new Date(day.getFullYear(), day.getMonth(), day.getDate())
    );
    
    if (!isSameDay) return null;
    
    const startHour = shift.startTime.getHours();
    const endHour = shift.endTime.getHours();
    const startMinutes = shift.startTime.getMinutes();
    const endMinutes = shift.endTime.getMinutes();
    
    const top = (startHour - 8) * HOUR_HEIGHT + (startMinutes / 60) * HOUR_HEIGHT;
    const height = (endHour - startHour) * HOUR_HEIGHT + ((endMinutes - startMinutes) / 60) * HOUR_HEIGHT;
    
    return { top, height };
  };

  // Get employee details by ID
  const getEmployeeById = (id: string) => {
    return employees.find(employee => employee.id === id);
  };

  // Render each day column in the calendar
  const renderDayColumn = (day: Date, index: number) => {
    const dayShifts = currentWeekShifts.filter(shift => 
      isEqual(
        new Date(shift.startTime.getFullYear(), shift.startTime.getMonth(), shift.startTime.getDate()),
        new Date(day.getFullYear(), day.getMonth(), day.getDate())
      )
    );
    
    const isToday = isEqual(
      new Date(day.getFullYear(), day.getMonth(), day.getDate()),
      new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())
    );
    
    return (
      <div key={index} className="flex-1 min-w-[150px]">
        <div 
          className={`sticky top-0 z-10 px-3 py-2 text-center border-b font-medium ${
            isToday ? 'bg-primary/10' : 'bg-background'
          }`}
        >
          <div className="text-xs uppercase text-muted-foreground">
            {format(day, 'EEEE', { locale: nl })}
          </div>
          <div className="text-sm">
            {format(day, 'd MMMM', { locale: nl })}
          </div>
          
          {/* Employee shift signup button */}
          {user && !isAdmin && !isManager && (
            <div className="mt-1">
              <ShiftSignup 
                date={day} 
                onShiftRequest={(start, end, notes) => handleShiftRequest(day, start, end, notes)} 
              />
            </div>
          )}
        </div>
        
        <div className="relative">
          {HOURS.map(hour => (
            <div 
              key={hour} 
              className={`border-b border-r h-[60px] relative group ${(isAdmin || isManager) ? 'cursor-pointer' : ''}`}
              onClick={(isAdmin || isManager) ? () => handleCellClick(day, hour) : undefined}
            >
              {(isAdmin || isManager) && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCellClick(day, hour);
                  }}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
          
          {dayShifts.map(shift => {
            const position = getShiftPosition(shift, day);
            const employee = getEmployeeById(shift.employeeId);
            
            if (!position) return null;

            const isPendingRequest = shift.position?.includes('Aangevraagd');
            
            return (
              <div
                key={shift.id}
                className={`absolute left-0 right-1 px-2 py-1 rounded-md ${
                  isPendingRequest 
                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
                    : 'bg-primary/90 text-primary-foreground border border-primary/30'
                } overflow-hidden cursor-pointer hover:z-10 hover:shadow-lg transition-all`}
                style={{ 
                  top: `${position.top}px`, 
                  height: `${position.height}px`, 
                  maxHeight: `${position.height}px` 
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Handle edit shift (not implemented in this version)
                  toast.info('Bewerken van diensten komt in een volgende versie');
                }}
              >
                <div className="flex items-center space-x-1 opacity-80 mb-1">
                  <Clock className="h-3 w-3" />
                  <span className="text-xs">
                    {format(shift.startTime, 'HH:mm')} - {format(shift.endTime, 'HH:mm')}
                  </span>
                </div>
                
                {employee && (
                  <div className="flex items-center gap-1">
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="text-[8px]">
                        {employee.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-xs font-medium truncate">
                      {employee.name}
                    </div>
                  </div>
                )}
                
                <div className="text-xs opacity-80 truncate">
                  {shift.position}
                </div>
                
                {shift.notes && (
                  <div className="text-xs italic opacity-70 truncate mt-1">
                    {shift.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4 pb-2 border-b">
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={goToPreviousWeek}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={goToNextWeek}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            onClick={goToToday}
            className="ml-2"
          >
            Vandaag
          </Button>
        </div>
        
        <h2 className="text-lg font-semibold">{formatWeekRange()}</h2>
        
        <Dialog open={isNewShiftDialogOpen} onOpenChange={setIsNewShiftDialogOpen}>
          <DialogTrigger asChild>
            {(isAdmin || isManager) && (
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Nieuwe dienst
              </Button>
            )}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nieuwe dienst toevoegen</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="employee">Medewerker</Label>
                <Select
                  value={newShift.employeeId?.toString()}
                  onValueChange={handleEmployeeSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer medewerker" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(employee => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startTime">Start tijd</Label>
                  <Input
                    id="startTime"
                    type="time"
                    value={format(newShift.startTime as Date, 'HH:mm')}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value.split(':').map(Number);
                      const newDate = new Date(selectedDate as Date);
                      newDate.setHours(hours, minutes, 0, 0);
                      handleShiftInputChange('startTime', newDate);
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endTime">Eind tijd</Label>
                  <Input
                    id="endTime"
                    type="time"
                    value={format(newShift.endTime as Date, 'HH:mm')}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value.split(':').map(Number);
                      const newDate = new Date(selectedDate as Date);
                      newDate.setHours(hours, minutes, 0, 0);
                      handleShiftInputChange('endTime', newDate);
                    }}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="position">Functie</Label>
                <Input
                  id="position"
                  value={newShift.position}
                  onChange={(e) => handleShiftInputChange('position', e.target.value)}
                  placeholder="Functie voor deze dienst"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Notities</Label>
                <Input
                  id="notes"
                  value={newShift.notes}
                  onChange={(e) => handleShiftInputChange('notes', e.target.value)}
                  placeholder="Optionele notities"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsNewShiftDialogOpen(false)}>
                Annuleren
              </Button>
              <Button onClick={handleSaveShift}>
                Opslaan
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="flex-1 overflow-auto">
        <div className="flex border-l">
          {/* Time column */}
          <div className="w-16 shrink-0">
            <div className="sticky top-0 z-10 h-[44px] bg-background border-b"></div>
            <div>
              {HOURS.map(hour => (
                <div key={hour} className="border-b border-r h-[60px] text-xs text-muted-foreground px-2">
                  <div className="relative -top-2.5">
                    {hour}:00
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Day columns */}
          {weekDays.map(renderDayColumn)}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
